import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// EN
import en_common from 'locales/en/common.json';
import en_activities from 'locales/en/activities.json';
import en_adm from 'locales/en/adm.json';
import en_auth from 'locales/en/auth.json';
import en_config from 'locales/en/config.json';
import en_disciplinas from 'locales/en/disciplinas.json';
import en_editObra from 'locales/en/editObra.json';
import en_faturamento from 'locales/en/faturamento.json';
import en_federatedViewer from 'locales/en/federatedViewer.json';
import en_gerenciarUsuarios from 'locales/en/gerenciarUsuarios.json';
import en_listagemArquivos from 'locales/en/listagemArquivos.json';
import en_messages from 'locales/en/messages.json';
import en_minhaConta from 'locales/en/minhaConta.json';
import en_notification from 'locales/en/notification.json';
import en_obras from 'locales/en/obras.json';
import en_panels from 'locales/en/panels.json';
import en_personalization from 'locales/en/personalization.json';
import en_plotagens from 'locales/en/plotagens.json';
import en_sharedModals from 'locales/en/sharedModals.json';
import en_sidebar from 'locales/en/sidebar.json';
import en_sobre from 'locales/en/sobre.json';
import en_viewer from 'locales/en/viewer.json';

// PT-BR
import ptBR_common from 'locales/pt-BR/common.json';
import ptBR_activities from 'locales/pt-BR/activities.json';
import ptBR_adm from 'locales/pt-BR/adm.json';
import ptBR_auth from 'locales/pt-BR/auth.json';
import ptBR_config from 'locales/pt-BR/config.json';
import ptBR_disciplinas from 'locales/pt-BR/disciplinas.json';
import ptBR_editObra from 'locales/pt-BR/editObra.json';
import ptBR_faturamento from 'locales/pt-BR/faturamento.json';
import ptBR_federatedViewer from 'locales/pt-BR/federatedViewer.json';
import ptBR_gerenciarUsuarios from 'locales/pt-BR/gerenciarUsuarios.json';
import ptBR_listagemArquivos from 'locales/pt-BR/listagemArquivos.json';
import ptBR_messages from 'locales/pt-BR/messages.json';
import ptBR_minhaConta from 'locales/pt-BR/minhaConta.json';
import ptBR_notification from 'locales/pt-BR/notification.json';
import ptBR_obras from 'locales/pt-BR/obras.json';
import ptBR_panels from 'locales/pt-BR/panels.json';
import ptBR_personalization from 'locales/pt-BR/personalization.json';
import ptBR_plotagens from 'locales/pt-BR/plotagens.json';
import ptBR_sharedModals from 'locales/pt-BR/sharedModals.json';
import ptBR_sidebar from 'locales/pt-BR/sidebar.json';
import ptBR_sobre from 'locales/pt-BR/sobre.json';
import ptBR_viewer from 'locales/pt-BR/viewer.json';

export const LANGUAGE_STORAGE_KEY = 'language';

export const SUPPORTED_LANGUAGES: Array<{ value: string; label: string }> = [
  { value: 'pt-BR', label: 'Português (Brasil)' },
  { value: 'en', label: 'English' },
];

const SUPPORTED_LANGUAGE_CODES = SUPPORTED_LANGUAGES.map((l) => l.value);

const detectBrowserLanguage = (): string => {
  if (typeof navigator === 'undefined') {
    return 'pt-BR';
  }
  const candidates = [navigator.language, ...(navigator.languages ?? [])];
  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }
    const lower = candidate.toLowerCase();
    if (lower.startsWith('pt')) {
      return 'pt-BR';
    }
    if (lower.startsWith('en')) {
      return 'en';
    }
  }
  return 'pt-BR';
};

export const resolveInitialLanguage = (): string => {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGE_CODES.includes(stored)) {
      return stored;
    }
  } catch {
    // localStorage may be unavailable (SSR, privacy mode) — fall through to browser detection.
  }
  return detectBrowserLanguage();
};

i18n.use(initReactI18next).init({
  resources: {
    en: {
      common: en_common,
      activities: en_activities,
      adm: en_adm,
      auth: en_auth,
      config: en_config,
      disciplinas: en_disciplinas,
      editObra: en_editObra,
      faturamento: en_faturamento,
      federatedViewer: en_federatedViewer,
      gerenciarUsuarios: en_gerenciarUsuarios,
      listagemArquivos: en_listagemArquivos,
      messages: en_messages,
      minhaConta: en_minhaConta,
      notification: en_notification,
      obras: en_obras,
      panels: en_panels,
      personalization: en_personalization,
      plotagens: en_plotagens,
      sharedModals: en_sharedModals,
      sidebar: en_sidebar,
      sobre: en_sobre,
      viewer: en_viewer,
    },
    'pt-BR': {
      common: ptBR_common,
      activities: ptBR_activities,
      adm: ptBR_adm,
      auth: ptBR_auth,
      config: ptBR_config,
      disciplinas: ptBR_disciplinas,
      editObra: ptBR_editObra,
      faturamento: ptBR_faturamento,
      federatedViewer: ptBR_federatedViewer,
      gerenciarUsuarios: ptBR_gerenciarUsuarios,
      listagemArquivos: ptBR_listagemArquivos,
      messages: ptBR_messages,
      minhaConta: ptBR_minhaConta,
      notification: ptBR_notification,
      obras: ptBR_obras,
      panels: ptBR_panels,
      personalization: ptBR_personalization,
      plotagens: ptBR_plotagens,
      sharedModals: ptBR_sharedModals,
      sidebar: ptBR_sidebar,
      sobre: ptBR_sobre,
      viewer: ptBR_viewer,
    },
  },
  lng: resolveInitialLanguage(),
  fallbackLng: 'pt-BR',
  defaultNS: 'common',
  interpolation: {
    escapeValue: false,
  },
});

export default i18n;
